import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useSubdomainOrganization } from '@/hooks/useSubdomainOrganization';
import { Building2, RefreshCw, Code, Users } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

const DevOrganizationTester = () => {
  const { organization: currentOrg, subdomain, isLoading } = useSubdomainOrganization();
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');

  // Fetch all organizations for testing
  const { data: organizations, isLoading: orgsLoading } = useQuery({
    queryKey: ['all-organizations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data;
    },
  });

  const handleSwitchOrganization = (orgSubdomain: string) => {
    // Save to localStorage for development
    localStorage.setItem('dev-organization', orgSubdomain);
    
    // Add URL parameter and reload
    const url = new URL(window.location.href);
    url.searchParams.set('org', orgSubdomain);
    window.location.href = url.toString();
  };

  const handleClearDevSettings = () => {
    localStorage.removeItem('dev-organization');
    const url = new URL(window.location.href);
    url.searchParams.delete('org');
    
    toast({
      title: "Development Settings Cleared",
      description: "Organization will now be detected from subdomain",
    });
    
    setTimeout(() => {
      window.location.href = url.toString();
    }, 1000);
  };

  const getCurrentOrgInfo = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const orgParam = urlParams.get('org');
    const storedOrg = localStorage.getItem('dev-organization');
    
    return {
      urlParam: orgParam,
      storedOrg: storedOrg,
      detectedSubdomain: subdomain,
      currentOrg: currentOrg,
    };
  };

  const orgInfo = getCurrentOrgInfo();

  // Only show in development
  if (window.location.hostname !== 'localhost' && !window.location.hostname.includes('127.0.0.1')) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-destructive">Development Only</CardTitle>
            <CardDescription>
              This page is only available in development mode.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Organization Tester</h1>
          <p className="text-muted-foreground">
            Development tool for testing subdomain-based organization detection
          </p>
        </div>
        <Badge variant="outline" className="gap-2">
          <Code className="h-4 w-4" />
          Development Mode
        </Badge>
      </div>

      {/* Current Organization Status */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Current Organization
            </CardTitle>
            <CardDescription>
              Currently active organization and detection method
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoading ? (
              <div className="text-center py-4">
                <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Loading organization...</p>
              </div>
            ) : currentOrg ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{currentOrg.display_name || currentOrg.name}</span>
                  <Badge variant="secondary">{currentOrg.subdomain}</Badge>
                </div>
                <div className="text-sm space-y-1">
                  <p><strong>ID:</strong> {currentOrg.id}</p>
                  <p><strong>Subdomain:</strong> {currentOrg.subdomain}</p>
                  <p><strong>Status:</strong> {currentOrg.is_active ? 'Active' : 'Inactive'}</p>
                </div>
              </div>
            ) : (
              <p className="text-destructive">No organization found</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Detection Debug Info</CardTitle>
            <CardDescription>
              How the organization was detected
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>URL Parameter:</span>
                <Badge variant={orgInfo.urlParam ? 'default' : 'outline'}>
                  {orgInfo.urlParam || 'None'}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span>localStorage:</span>
                <Badge variant={orgInfo.storedOrg ? 'default' : 'outline'}>
                  {orgInfo.storedOrg || 'None'}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span>Detected Subdomain:</span>
                <Badge variant="secondary">
                  {orgInfo.detectedSubdomain}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span>Hostname:</span>
                <Badge variant="outline">
                  {window.location.hostname}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Organization Switcher */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Switch Organization
          </CardTitle>
          <CardDescription>
            Test different organizations in development mode
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {orgsLoading ? (
            <div className="text-center py-4">
              <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Loading organizations...</p>
            </div>
          ) : (
            <>
              <div className="flex items-center space-x-4">
                <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select an organization to test" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations?.map((org) => (
                      <SelectItem key={org.id} value={org.subdomain}>
                        <div className="flex items-center justify-between w-full">
                          <span>{org.display_name || org.name}</span>
                          <Badge variant="outline" className="ml-2">
                            {org.subdomain}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Button 
                  onClick={() => selectedOrgId && handleSwitchOrganization(selectedOrgId)}
                  disabled={!selectedOrgId}
                >
                  Switch
                </Button>
              </div>
              
              <div className="flex space-x-2">
                <Button 
                  variant="outline" 
                  onClick={handleClearDevSettings}
                >
                  Clear Dev Settings
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => window.location.reload()}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reload Page
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Available Organizations */}
      <Card>
        <CardHeader>
          <CardTitle>Available Organizations</CardTitle>
          <CardDescription>
            All active organizations in the system
          </CardDescription>
        </CardHeader>
        <CardContent>
          {orgsLoading ? (
            <div className="text-center py-4">
              <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Loading organizations...</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {organizations?.map((org) => (
                <div 
                  key={org.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    org.subdomain === subdomain ? 'bg-primary/5 border-primary' : 'hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Building2 className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{org.display_name || org.name}</p>
                      <p className="text-sm text-muted-foreground">{org.subdomain}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {org.subdomain === subdomain && (
                      <Badge variant="default">Current</Badge>
                    )}
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleSwitchOrganization(org.subdomain)}
                      disabled={org.subdomain === subdomain}
                    >
                      {org.subdomain === subdomain ? 'Active' : 'Switch'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DevOrganizationTester;